import { Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';

import { PageSpinner } from 'common/components/cookbook/PageSpinner';
import { LocalStorageWarehouseProvider } from 'common/context/WarehouseContext';
import { useUser } from 'common/hooks/useUser';
import { useWarpMerchant } from 'common/hooks/useWarpMerchant';
import { useWarpMerchantsForUser } from 'common/hooks/useWarpMerchantsForUser';
import { getTenantRoutePath } from 'common/utils/getTenantRoutePath';
import { setReturnPath } from 'common/utils/returnPath';

import { MerchantRedirect } from './MerchantRedirect';
import { getTenantConfig } from '../../config/tenantConfig';

export function MerchantVerification() {
  const { data: merchants, isLoading } = useWarpMerchantsForUser();
  const { merchant, setMerchant } = useWarpMerchant();
  const navigate = useNavigate();

  const pathname = useLocation({ select: (location) => location.pathname });
  const tenantId = merchant?.id ?? '';

  const routePath = getTenantRoutePath(tenantId, pathname);
  const merchantConfig = getTenantConfig(tenantId);

  const { user } = useUser();
  const userRoles = user?.roles ?? [];

  const routeAvailable = routePath
    ? (merchantConfig.routes[routePath] || []).some((r) => userRoles.includes(r))
    : pathname === '/';

  const shouldAutoSetMerchant = merchants?.length === 1 && !merchant;

  useEffect(() => {
    if (shouldAutoSetMerchant) {
      setMerchant(merchants[0]);
    }
  }, [merchants, shouldAutoSetMerchant, setMerchant]);

  useEffect(() => {
    if (merchant && merchants) {
      const matchingMerchant = merchants.find(({ id }) => id === merchant.id);
      if (!matchingMerchant) {
        setMerchant(null);
      }
    }
  }, [merchants, merchant, setMerchant]);

  useEffect(() => {
    const needsSelection = !merchant && merchants && merchants.length > 1;
    const noMerchantsAvailable = merchants && merchants.length === 0;

    if (needsSelection || noMerchantsAvailable) {
      setReturnPath();
      void navigate({ to: '/merchant-selection' });
    }
  }, [merchants, merchant, navigate]);

  if (isLoading || !merchant) {
    return <PageSpinner />;
  }

  if (merchant && !routeAvailable) {
    return <MerchantRedirect />;
  }

  if (merchant) {
    return (
      <LocalStorageWarehouseProvider>
        <Outlet />
      </LocalStorageWarehouseProvider>
    );
  }

  // Fallback while navigation effects are in progress
  return <PageSpinner />;
}
